import { useEffect, useMemo, useRef, useState } from "react";

import type { ClipRef, Track } from "../../api/projects";
import { useEditor } from "../../store/editor";
import type { MediaClip } from "../../api/media";
import ContextMenu, { type ContextMenuItem } from "./ContextMenu";
import TrackHeader from "./TrackHeader";
import { MEDIA_DRAG_MIME, type MediaDragPayload } from "./MediaLibrary";
import { TRANSITION_DRAG_MIME } from "./TransitionsPanel";
import TransitionsPanel from "./TransitionsPanel";
import { applyEffectPresetToClip } from "./EffectsPanel";
import { MUSIC_DRAG_MIME } from "./MusicPanel";
import { EFFECT_DRAG_MIME, EFFECT_PRESETS } from "../../lib/effectPresets";
import { defaultDurationMs, findTransition } from "../../lib/transitions";
import Waveform from "./Waveform";

/**
 * Para que el indicador de drop sepa el tipo del medio (vídeo/audio) durante
 * dragover (donde getData() está restringido), publicamos un MIME extra
 * "application/x-videotool-media+<tipo>" desde MediaLibrary.
 */
function detectDragType(e: React.DragEvent): "video" | "audio" | "image" | null {
  const types = Array.from(e.dataTransfer.types);
  if (types.includes("application/x-videotool-media+video")) return "video";
  if (types.includes("application/x-videotool-media+audio")) return "audio";
  if (types.includes("application/x-videotool-media+image")) return "image";
  return null;
}

const DEFAULT_PX_PER_SEC = 80;
const MIN_PX_PER_SEC = 20;
const MAX_PX_PER_SEC = 400;
const LANE_HEIGHT = 30;
const LANE_GAP = 2;
const TRACK_PAD = 4;
const SNAP_PX = 8;
const HEADER_WIDTH = 140;
const RULER_HEIGHT = 18;

function computeLanes(clips: ClipRef[]): { laneById: Record<string, number>; laneCount: number } {
  const sorted = [...clips].sort((a, b) => a.start - b.start);
  const laneEnds: number[] = [];
  const laneById: Record<string, number> = {};
  for (const c of sorted) {
    const end = c.start + (c.out - c.in);
    let placed = false;
    for (let i = 0; i < laneEnds.length; i++) {
      if (laneEnds[i] <= c.start) {
        laneEnds[i] = end;
        laneById[c.id] = i;
        placed = true;
        break;
      }
    }
    if (!placed) {
      laneById[c.id] = laneEnds.length;
      laneEnds.push(end);
    }
  }
  return { laneById, laneCount: Math.max(1, laneEnds.length) };
}

function findSnap(candidateMs: number, targets: number[], thresholdMs: number): number | null {
  let best: number | null = null;
  let bestDist = thresholdMs;
  for (const t of targets) {
    const d = Math.abs(t - candidateMs);
    if (d <= bestDist) { bestDist = d; best = t; }
  }
  return best;
}

function findClipAndTrack(timeline: any, id: string): { clip: ClipRef; track: Track } | null {
  for (const t of timeline.tracks) {
    const c = t.clips.find((c: ClipRef) => c.id === id);
    if (c) return { clip: c, track: t };
  }
  return null;
}

function trackHeightOf(laneCount: number): number {
  return TRACK_PAD * 2 + laneCount * LANE_HEIGHT + (laneCount - 1) * LANE_GAP;
}

type DragState =
  | { kind: "move"; ids: string[]; startX: number; origStarts: Record<string, number>;
      anchorId: string; anchorLength: number; sourceTrackId: string }
  | { kind: "trim"; id: string; side: "in" | "out"; startX: number;
      origIn: number; origOut: number; origStart: number }
  | { kind: "rubber"; startX: number; startY: number };

interface RubberRect { x: number; y: number; w: number; h: number }

export default function Timeline(props: {
  mediaByClipId?: Record<number, MediaClip>;
}) {
  const mediaByClipId = props.mediaByClipId || {};
  const timeline = useEditor((s) => s.timeline);
  const selectedIds = useEditor((s) => s.selectedClipIds);
  const selectOne = useEditor((s) => s.selectOne);
  const toggleSelect = useEditor((s) => s.toggleSelect);
  const addToSelection = useEditor((s) => s.addToSelection);
  const selectMany = useEditor((s) => s.selectMany);
  const clearSelection = useEditor((s) => s.clearSelection);
  const moveClip = useEditor((s) => s.moveClip);
  const resizeClip = useEditor((s) => s.resizeClip);
  const removeSelected = useEditor((s) => s.removeSelected);
  const duplicateSelection = useEditor((s) => s.duplicateSelection);
  const copySelection = useEditor((s) => s.copySelection);
  const cutSelection = useEditor((s) => s.cutSelection);
  const pasteAtPlayhead = useEditor((s) => s.pasteAtPlayhead);
  const setClipTrack = useEditor((s) => s.setClipTrack);
  const addTrack = useEditor((s) => s.addTrack);
  const addClipAt = useEditor((s) => s.addClipAt);
  const playhead = useEditor((s) => s.playhead_ms);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const duration = useEditor((s) => s.durationMs());

  const [pxPerSec, setPxPerSec] = useState(DEFAULT_PX_PER_SEC);
  const containerRef = useRef<HTMLDivElement>(null);
  const tracksRootRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [snapLineMs, setSnapLineMs] = useState<number | null>(null);
  const [rubber, setRubber] = useState<RubberRect | null>(null);
  const [hoverTrackId, setHoverTrackId] = useState<string | null>(null);
  const [mediaDrop, setMediaDrop] = useState<{
    timeMs: number; trackId: string; dragType: "video" | "audio" | "image";
  } | null>(null);
  const [trDrop, setTrDrop] = useState<string | null>(null);  // id del clip al que apunta el drop de transición
  const [hoverEndOfClip, setHoverEndOfClip] = useState<string | null>(null);
  const [trPickerForClip, setTrPickerForClip] = useState<string | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number; clipId: string | null } | null>(null);

  const setTimeline = useEditor((s) => s.setTimeline);
  function applyTransitionTo(clipId: string, transitionId: string) {
    const tl = useEditor.getState().timeline;
    const tracks = tl.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === clipId
        ? { ...c, transitionOut: { type: transitionId, duration_ms: defaultDurationMs(transitionId) } }
        : c),
    }));
    setTimeline({ ...tl, tracks });
  }

  const totalSec = Math.max(10, Math.ceil(duration / 1000) + 5);
  const totalPx = totalSec * pxPerSec;
  const thresholdMs = (SNAP_PX / pxPerSec) * 1000;

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const snapTargets = useMemo(() => {
    const t: number[] = [0, playhead];
    for (let s = 0; s <= totalSec; s++) t.push(s * 1000);
    for (const track of timeline.tracks) {
      for (const c of track.clips) {
        if (drag && drag.kind === "move" && drag.ids.includes(c.id)) continue;
        if (drag && drag.kind === "trim" && drag.id === c.id) continue;
        t.push(c.start);
        t.push(c.start + (c.out - c.in));
      }
    }
    return t;
  }, [timeline.tracks, playhead, totalSec, drag]);

  const trackLayouts = useMemo(
    () => timeline.tracks.map((t) => ({ track: t, ...computeLanes(t.clips) })),
    [timeline.tracks],
  );

  /** Calcula qué pista contiene la coordenada Y relativa al área de tracks. */
  function trackAtY(yRel: number): string | null {
    let cursor = RULER_HEIGHT;
    for (const { track, laneCount } of trackLayouts) {
      const h = trackHeightOf(laneCount);
      if (yRel >= cursor && yRel < cursor + h) return track.id;
      cursor += h;
    }
    return null;
  }

  // ===== Mouse drag (move / trim / rubber band) ============================
  useEffect(() => {
    if (!drag) return;
    document.body.style.cursor =
      drag.kind === "trim" ? "ew-resize" :
      drag.kind === "move" ? "grabbing" : "crosshair";
    document.body.style.userSelect = "none";

    function onMove(e: MouseEvent) {
      if (drag!.kind === "move") {
        const deltaPx = e.clientX - drag!.startX;
        const deltaMs = (deltaPx / pxPerSec) * 1000;
        const anchorOrigStart = drag!.origStarts[drag!.anchorId];
        let proposedAnchorStart = anchorOrigStart + deltaMs;
        let snapMs: number | null = null;
        if (!e.altKey) {
          const snapL = findSnap(proposedAnchorStart, snapTargets, thresholdMs);
          const snapR = findSnap(proposedAnchorStart + drag!.anchorLength, snapTargets, thresholdMs);
          const dL = snapL !== null ? Math.abs(snapL - proposedAnchorStart) : Infinity;
          const dR = snapR !== null ? Math.abs(snapR - (proposedAnchorStart + drag!.anchorLength)) : Infinity;
          if (dL <= dR && snapL !== null) {
            proposedAnchorStart = snapL;
            snapMs = snapL;
          } else if (snapR !== null) {
            proposedAnchorStart = snapR - drag!.anchorLength;
            snapMs = snapR;
          }
        }
        const effectiveDelta = proposedAnchorStart - anchorOrigStart;
        setSnapLineMs(snapMs);
        for (const id of drag!.ids) {
          moveClip(id, drag!.origStarts[id] + effectiveDelta);
        }

        // Detección de pista destino (solo si arrastras 1 clip)
        if (drag!.ids.length === 1) {
          const rect = tracksRootRef.current?.getBoundingClientRect();
          if (rect) {
            const yRel = e.clientY - rect.top;
            const overId = trackAtY(yRel);
            if (overId && overId !== drag!.sourceTrackId) {
              const sourceTrack = timeline.tracks.find((t) => t.id === drag!.sourceTrackId);
              const targetTrack = timeline.tracks.find((t) => t.id === overId);
              if (sourceTrack && targetTrack && sourceTrack.type === targetTrack.type) {
                setHoverTrackId(overId);
              } else {
                setHoverTrackId(null);
              }
            } else {
              setHoverTrackId(null);
            }
          }
        }
      } else if (drag!.kind === "trim") {
        const deltaPx = e.clientX - drag!.startX;
        const deltaMs = (deltaPx / pxPerSec) * 1000;
        if (drag!.side === "in") {
          let newEdge = drag!.origStart + deltaMs;
          let snapMs: number | null = null;
          if (!e.altKey) {
            const snap = findSnap(newEdge, snapTargets, thresholdMs);
            if (snap !== null) { newEdge = snap; snapMs = snap; }
          }
          const shift = newEdge - drag!.origStart;
          const targetIn = drag!.origIn + shift;
          const clampedIn = Math.max(0, Math.min(drag!.origOut - 100, targetIn));
          setSnapLineMs(snapMs);
          const currentClip = findClipAndTrack(timeline, drag!.id)?.clip;
          if (currentClip) {
            const deltaIn = clampedIn - currentClip.in;
            if (deltaIn !== 0) resizeClip(drag!.id, "in", deltaIn);
          }
        } else {
          let newEdge = drag!.origStart + (drag!.origOut - drag!.origIn) + deltaMs;
          let snapMs: number | null = null;
          if (!e.altKey) {
            const snap = findSnap(newEdge, snapTargets, thresholdMs);
            if (snap !== null) { newEdge = snap; snapMs = snap; }
          }
          const newOut = drag!.origIn + (newEdge - drag!.origStart);
          const clampedOut = Math.max(drag!.origIn + 100, newOut);
          setSnapLineMs(snapMs);
          const currentClip = findClipAndTrack(timeline, drag!.id)?.clip;
          if (currentClip) {
            const deltaOut = clampedOut - currentClip.out;
            if (deltaOut !== 0) resizeClip(drag!.id, "out", deltaOut);
          }
        }
      } else if (drag!.kind === "rubber") {
        const rect = tracksRootRef.current?.getBoundingClientRect();
        if (!rect) return;
        const scroll = containerRef.current?.scrollLeft || 0;
        const sx = drag!.startX - rect.left + scroll;
        const sy = drag!.startY - rect.top;
        const cx = e.clientX - rect.left + scroll;
        const cy = e.clientY - rect.top;
        const x = Math.min(sx, cx), y = Math.min(sy, cy);
        const w = Math.abs(cx - sx), h = Math.abs(cy - sy);
        setRubber({ x, y, w, h });
      }
    }

    function onUp() {
      if (drag!.kind === "rubber" && rubber) {
        const startMs = (rubber.x / pxPerSec) * 1000;
        const endMs = ((rubber.x + rubber.w) / pxPerSec) * 1000;
        const ids: string[] = [];
        let yCursor = RULER_HEIGHT;
        for (const { track, laneCount } of trackLayouts) {
          const trackH = trackHeightOf(laneCount);
          const trackTop = yCursor;
          const trackBottom = yCursor + trackH;
          const overlapsVert = !(rubber.y + rubber.h < trackTop || rubber.y > trackBottom);
          if (overlapsVert) {
            for (const c of track.clips) {
              const ce = c.start + (c.out - c.in);
              if (ce >= startMs && c.start <= endMs) ids.push(c.id);
            }
          }
          yCursor += trackH;
        }
        selectMany(ids);
      }
      // Confirmar cambio de pista si aplica
      if (drag!.kind === "move" && hoverTrackId && drag!.ids.length === 1) {
        setClipTrack(drag!.anchorId, hoverTrackId);
      }
      setDrag(null);
      setSnapLineMs(null);
      setRubber(null);
      setHoverTrackId(null);
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [drag, pxPerSec, snapTargets, thresholdMs, moveClip, resizeClip, selectMany,
      setClipTrack, trackLayouts, timeline, rubber, hoverTrackId]);

  // ===== Atajos zoom =======================================================
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "+" || e.key === "=") {
          e.preventDefault();
          setPxPerSec((p) => Math.min(MAX_PX_PER_SEC, Math.round(p * 1.25)));
        } else if (e.key === "-") {
          e.preventDefault();
          setPxPerSec((p) => Math.max(MIN_PX_PER_SEC, Math.round(p / 1.25)));
        } else if (e.key === "0") {
          e.preventDefault();
          setPxPerSec(DEFAULT_PX_PER_SEC);
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function onWheel(e: React.WheelEvent) {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setPxPerSec((p) => {
      const factor = e.deltaY > 0 ? 1 / 1.1 : 1.1;
      return Math.max(MIN_PX_PER_SEC, Math.min(MAX_PX_PER_SEC, Math.round(p * factor)));
    });
  }

  function onClipBodyMouseDown(e: React.MouseEvent, c: ClipRef) {
    e.stopPropagation();
    const found = findClipAndTrack(timeline, c.id);
    if (!found) return;
    let idsToDrag: string[];
    if (e.shiftKey) {
      addToSelection(c.id);
      idsToDrag = Array.from(new Set([...selectedIds, c.id]));
    } else if (e.ctrlKey || e.metaKey) {
      toggleSelect(c.id);
      idsToDrag = selectedSet.has(c.id)
        ? selectedIds.filter((x) => x !== c.id)
        : [...selectedIds, c.id];
    } else if (!selectedSet.has(c.id)) {
      selectOne(c.id);
      idsToDrag = [c.id];
    } else {
      idsToDrag = selectedIds;
    }
    const origStarts: Record<string, number> = {};
    for (const id of idsToDrag) {
      const f = findClipAndTrack(timeline, id);
      if (f) origStarts[id] = f.clip.start;
    }
    setDrag({
      kind: "move", ids: idsToDrag, startX: e.clientX,
      origStarts, anchorId: c.id, anchorLength: c.out - c.in,
      sourceTrackId: found.track.id,
    });
  }

  function onTrimMouseDown(e: React.MouseEvent, c: ClipRef, side: "in" | "out") {
    e.stopPropagation();
    selectOne(c.id);
    setDrag({
      kind: "trim", id: c.id, side, startX: e.clientX,
      origIn: c.in, origOut: c.out, origStart: c.start,
    });
  }

  function onClipContextMenu(e: React.MouseEvent, c: ClipRef) {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedSet.has(c.id)) selectOne(c.id);
    setCtx({ x: e.clientX, y: e.clientY, clipId: c.id });
  }

  function onRulerClick(e: React.MouseEvent) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left + (containerRef.current!.scrollLeft || 0);
    setPlayhead((x / pxPerSec) * 1000);
  }

  function onTracksBgMouseDown(e: React.MouseEvent) {
    if (e.button !== 0) return;
    if (e.shiftKey || e.ctrlKey || e.metaKey) return;
    clearSelection();
    setDrag({ kind: "rubber", startX: e.clientX, startY: e.clientY });
  }

  // ===== Drag desde la mediateca al timeline ===============================
  function onTimelineDragOver(e: React.DragEvent) {
    const types = Array.from(e.dataTransfer.types);

    // Drop de efecto sobre un clip
    if (types.some((t) => t.startsWith(EFFECT_DRAG_MIME))) {
      const rect = tracksRootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const scroll = containerRef.current?.scrollLeft || 0;
      const x = e.clientX - rect.left + scroll;
      const y = e.clientY - rect.top;
      const timeMs = Math.max(0, (x / pxPerSec) * 1000);
      for (const t of timeline.tracks) {
        for (const c of t.clips) {
          if (timeMs >= c.start && timeMs <= c.start + (c.out - c.in)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
            setTrDrop(c.id);
            void y;
            return;
          }
        }
      }
      setTrDrop(null);
      return;
    }

    // Drop de música sobre pista audio (cualquier punto)
    if (types.some((t) => t.startsWith(MUSIC_DRAG_MIME))) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      return;
    }

    // Drop de transición: prioritario
    if (types.some((t) => t.startsWith(TRANSITION_DRAG_MIME))) {
      // calculamos qué clip está bajo el cursor
      const rect = tracksRootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const scroll = containerRef.current?.scrollLeft || 0;
      const x = e.clientX - rect.left + scroll;
      const y = e.clientY - rect.top;
      const timeMs = Math.max(0, (x / pxPerSec) * 1000);
      // Buscar el clip que contenga este timeMs en cualquier pista
      for (const t of timeline.tracks) {
        for (const c of t.clips) {
          const cend = c.start + (c.out - c.in);
          if (timeMs >= c.start && timeMs <= cend) {
            // mejor coincidencia: cercano al final del clip
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
            setTrDrop(c.id);
            void y;  // unused
            return;
          }
        }
      }
      setTrDrop(null);
      return;
    }

    const dragType = detectDragType(e);
    if (!dragType) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    const rect = tracksRootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const scroll = containerRef.current?.scrollLeft || 0;
    const x = e.clientX - rect.left + scroll;
    const y = e.clientY - rect.top;
    const timeMs = Math.max(0, (x / pxPerSec) * 1000);
    const wantedType: "video" | "audio" = dragType === "audio" ? "audio" : "video";
    const overTrackId = trackAtY(y);
    const overTrack = timeline.tracks.find((t) => t.id === overTrackId);
    let targetTrackId: string | null = null;
    if (overTrack && overTrack.type === wantedType) targetTrackId = overTrack.id;
    else {
      const fb = timeline.tracks.find((t) => t.type === wantedType);
      if (fb) targetTrackId = fb.id;
    }
    if (targetTrackId) setMediaDrop({ timeMs, trackId: targetTrackId, dragType });
  }

  function onTimelineDragLeave(e: React.DragEvent) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setMediaDrop(null);
    setTrDrop(null);
  }

  /** Calcula qué clip está bajo la coordenada (x, y) relativa al tracksRoot. */
  function clipAtPoint(timeMs: number, yRel: number): string | null {
    let yCursor = RULER_HEIGHT;
    for (const { track, laneCount } of trackLayouts) {
      const trackH = trackHeightOf(laneCount);
      if (yRel >= yCursor && yRel < yCursor + trackH) {
        for (const c of track.clips) {
          const cend = c.start + (c.out - c.in);
          if (timeMs >= c.start && timeMs <= cend) return c.id;
        }
      }
      yCursor += trackH;
    }
    return null;
  }

  function onTimelineDrop(e: React.DragEvent) {
    setMediaDrop(null);

    // ===== Drop de efecto =================================================
    const effData = e.dataTransfer.getData(EFFECT_DRAG_MIME);
    if (effData) {
      e.preventDefault();
      const rect = tracksRootRef.current?.getBoundingClientRect();
      if (!rect) { setTrDrop(null); return; }
      const scroll = containerRef.current?.scrollLeft || 0;
      const x = e.clientX - rect.left + scroll;
      const y = e.clientY - rect.top;
      const timeMs = Math.max(0, (x / pxPerSec) * 1000);
      const targetId = clipAtPoint(timeMs, y);
      if (targetId) {
        try {
          const { id } = JSON.parse(effData);
          const preset = EFFECT_PRESETS.find((p) => p.id === id);
          if (preset) applyEffectPresetToClip(targetId, preset);
        } catch (err) { console.error("effect drop parse failed", err); }
      }
      setTrDrop(null);
      return;
    }

    // ===== Drop de música =================================================
    const musicData = e.dataTransfer.getData(MUSIC_DRAG_MIME);
    if (musicData) {
      e.preventDefault();
      try {
        const payload = JSON.parse(musicData);
        const rect = tracksRootRef.current?.getBoundingClientRect();
        if (!rect) return;
        const scroll = containerRef.current?.scrollLeft || 0;
        const x = e.clientX - rect.left + scroll;
        const y = e.clientY - rect.top;
        const timeMs = Math.max(0, (x / pxPerSec) * 1000);
        const overTrackId = trackAtY(y);
        const overTrack = timeline.tracks.find((t) => t.id === overTrackId);
        const trackId = overTrack && overTrack.type === "audio"
          ? overTrack.id
          : timeline.tracks.find((t) => t.type === "audio")?.id;
        addClipAt({
          mediaId: payload.mediaId, mediaType: "audio",
          durationMs: payload.durationMs, trackId, startMs: timeMs,
        });
      } catch (err) { console.error("music drop parse failed", err); }
      return;
    }

    const trData = e.dataTransfer.getData(TRANSITION_DRAG_MIME);
    // Calcular target clip DIRECTAMENTE del evento (sin depender de state que
    // pudo haber cambiado entre dragover y drop)
    const rect = tracksRootRef.current?.getBoundingClientRect();
    if (trData && rect) {
      e.preventDefault();
      const scroll = containerRef.current?.scrollLeft || 0;
      const x = e.clientX - rect.left + scroll;
      const y = e.clientY - rect.top;
      const timeMs = Math.max(0, (x / pxPerSec) * 1000);
      const targetId = clipAtPoint(timeMs, y);
      if (targetId) {
        try {
          const { id } = JSON.parse(trData);
          applyTransitionTo(targetId, id);
        } catch (err) {
          console.error("transition drop parse failed", err);
        }
      } else {
        console.warn("transition dropped outside any clip");
      }
      setTrDrop(null);
      return;
    }
    setTrDrop(null);

    const data = e.dataTransfer.getData(MEDIA_DRAG_MIME);
    if (!data) return;
    e.preventDefault();
    try {
      const payload: MediaDragPayload = JSON.parse(data);
      const rect = tracksRootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const scroll = containerRef.current?.scrollLeft || 0;
      const x = e.clientX - rect.left + scroll;
      const y = e.clientY - rect.top;
      const timeMs = Math.max(0, (x / pxPerSec) * 1000);
      const wantedType: "video" | "audio" = payload.mediaType === "audio" ? "audio" : "video";
      const overTrackId = trackAtY(y);
      const overTrack = timeline.tracks.find((t) => t.id === overTrackId);
      const trackId = overTrack && overTrack.type === wantedType
        ? overTrack.id
        : (timeline.tracks.find((t) => t.type === wantedType)?.id);
      addClipAt({
        mediaId: payload.mediaId, mediaType: payload.mediaType,
        durationMs: payload.durationMs, trackId, startMs: timeMs,
      });
    } catch (err) {
      console.error("drop parse failed", err);
    }
  }

  function ctxItems(): ContextMenuItem[] {
    const has = selectedIds.length > 0;
    const canPaste = useEditor.getState().clipboard.length > 0;
    return [
      { label: "Cortar en playhead", shortcut: "C", disabled: !has,
        onClick: () => useEditor.getState().splitSelectedOrUnderPlayhead() },
      { label: "Duplicar", shortcut: "Ctrl+D", disabled: !has, onClick: duplicateSelection },
      { divider: true, label: "", onClick: () => {} },
      { label: "Copiar", shortcut: "Ctrl+C", disabled: !has, onClick: copySelection },
      { label: "Cortar (al portapapeles)", shortcut: "Ctrl+X", disabled: !has, onClick: cutSelection },
      { label: "Pegar en playhead", shortcut: "Ctrl+V", disabled: !canPaste, onClick: pasteAtPlayhead },
      { divider: true, label: "", onClick: () => {} },
      { label: "Borrar", shortcut: "Supr", disabled: !has, danger: true, onClick: removeSelected },
    ];
  }

  const ruler = useMemo(() => {
    const step = pxPerSec >= 120 ? 1 : pxPerSec >= 60 ? 2 : pxPerSec >= 30 ? 5 : 10;
    const ticks: { sec: number; left: number }[] = [];
    for (let s = 0; s <= totalSec; s += step) ticks.push({ sec: s, left: s * pxPerSec });
    return ticks;
  }, [totalSec, pxPerSec]);

  return (
    <div style={{ outline: "none", height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "4px 8px", borderBottom: "1px solid var(--border)",
        fontSize: 11, color: "var(--text-dim)",
      }}>
        <span>Zoom</span>
        <button onClick={() => setPxPerSec((p) => Math.max(MIN_PX_PER_SEC, Math.round(p / 1.25)))}
                style={{ padding: "1px 8px", fontSize: 12 }}>−</button>
        <input type="range" min={MIN_PX_PER_SEC} max={MAX_PX_PER_SEC} value={pxPerSec}
               onChange={(e) => setPxPerSec(Number(e.target.value))} style={{ width: 120 }} />
        <button onClick={() => setPxPerSec((p) => Math.min(MAX_PX_PER_SEC, Math.round(p * 1.25)))}
                style={{ padding: "1px 8px", fontSize: 12 }}>+</button>
        <button onClick={() => setPxPerSec(DEFAULT_PX_PER_SEC)}
                style={{ padding: "1px 8px", fontSize: 11 }} title="Reset (Ctrl+0)">100%</button>
        <span style={{ marginLeft: 4 }}>{pxPerSec}px/s</span>
        <span style={{ marginLeft: 16, opacity: 0.7 }}>|</span>
        <button onClick={() => addTrack("video")} style={{ padding: "2px 8px", fontSize: 11 }}>
          + Pista vídeo
        </button>
        <button onClick={() => addTrack("audio")} style={{ padding: "2px 8px", fontSize: 11 }}>
          + Pista audio
        </button>
        <span style={{ marginLeft: "auto", opacity: 0.7 }}>
          C cortar · Supr borrar · Ctrl+C/V/X/D · Ctrl+A todo · Alt sin snap
        </span>
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        {/* Columna izquierda: cabeceras de pistas */}
        <div style={{
          width: HEADER_WIDTH, flexShrink: 0,
          borderRight: "1px solid var(--border)",
          display: "flex", flexDirection: "column",
          background: "var(--bg-elev)",
          overflowY: "hidden",
        }}>
          <div style={{ height: RULER_HEIGHT, borderBottom: "1px solid var(--border)" }} />
          {trackLayouts.map((tl, idx) => (
            <TrackHeader
              key={tl.track.id}
              track={tl.track}
              height={trackHeightOf(tl.laneCount)}
              isFirst={idx === 0}
              isLast={idx === trackLayouts.length - 1}
            />
          ))}
        </div>

        {/* Columna derecha: scroll horizontal */}
        <div
          ref={containerRef}
          className="timeline-tracks"
          onWheel={onWheel}
          style={{ overflowX: "auto", flex: 1, position: "relative" }}
        >
          <div
            ref={tracksRootRef}
            onMouseDown={onTracksBgMouseDown}
            onDragOver={onTimelineDragOver}
            onDragLeave={onTimelineDragLeave}
            onDrop={onTimelineDrop}
            style={{ width: totalPx, position: "relative", minHeight: 200 }}
          >
            <div className="timeline-ruler" onClick={onRulerClick}>
              {ruler.map((t) => (
                <div key={t.sec} style={{ position: "absolute", left: t.left, top: 0,
                                           borderLeft: "1px solid var(--border)", height: "100%",
                                           paddingLeft: 4 }}>
                  {t.sec}s
                </div>
              ))}
            </div>

            <div className="timeline-playhead" style={{ left: (playhead / 1000) * pxPerSec }} />

            {snapLineMs !== null && (
              <div style={{
                position: "absolute", top: 0, bottom: 0,
                left: (snapLineMs / 1000) * pxPerSec,
                width: 1, background: "#f5d04a",
                boxShadow: "0 0 4px #f5d04a",
                pointerEvents: "none", zIndex: 11,
              }} />
            )}

            {rubber && (
              <div style={{
                position: "absolute",
                left: rubber.x, top: rubber.y, width: rubber.w, height: rubber.h,
                border: "1px solid var(--accent)",
                background: "rgba(91,199,255,0.12)",
                pointerEvents: "none", zIndex: 12,
              }} />
            )}

            {mediaDrop && (
              <div style={{
                position: "absolute", top: 0, bottom: 0,
                left: (mediaDrop.timeMs / 1000) * pxPerSec,
                width: 2, background: "var(--accent)",
                boxShadow: "0 0 6px var(--accent)",
                pointerEvents: "none", zIndex: 13,
              }} />
            )}

            {trackLayouts.map(({ track, laneById, laneCount }) => {
              const trackHeight = trackHeightOf(laneCount);
              const isHovered = hoverTrackId === track.id;
              const isDropTarget = mediaDrop?.trackId === track.id;
              const showAccent = isHovered || isDropTarget;
              return (
                <div
                  key={track.id}
                  className={`timeline-track ${track.type}`}
                  style={{
                    height: trackHeight,
                    background: showAccent ? "rgba(91,199,255,0.15)" : undefined,
                    boxShadow: showAccent ? "inset 0 0 0 1px var(--accent)" : undefined,
                  }}
                >
                  {track.clips.map((c) => {
                    const len = c.out - c.in;
                    const left = (c.start / 1000) * pxPerSec;
                    const width = Math.max(20, (len / 1000) * pxPerSec);
                    const lane = laneById[c.id] ?? 0;
                    const top = TRACK_PAD + lane * (LANE_HEIGHT + LANE_GAP);
                    const isSelected = selectedSet.has(c.id);
                    const isTrDropTarget = trDrop === c.id;
                    const media = mediaByClipId[c.clipId];
                    const showWaveform = media && track.type === "audio"
                      && (media.proxy_url || media.original_url);
                    const cEnd = c.start + (c.out - c.in);
                    const nextClip = track.clips.find((x) =>
                      x.id !== c.id && Math.abs(x.start - cEnd) < 100);
                    const hasTransition = !!c.transitionOut?.type;
                    const showAddButton = (hoverEndOfClip === c.id || isTrDropTarget) && !hasTransition && nextClip;
                    const rightEdgeX = left + width;
                    return (
                      <div key={c.id} style={{ display: "contents" }}>
                      <div
                        className={`timeline-clip ${track.type} ${isSelected ? "selected" : ""}`}
                        style={{
                          left, width, top, height: LANE_HEIGHT, bottom: "auto",
                          display: "flex", alignItems: "stretch", padding: 0,
                          position: "absolute",
                          outline: isTrDropTarget ? "2px solid #4ade80" : undefined,
                          boxShadow: isTrDropTarget ? "0 0 12px #4ade80" : undefined,
                        }}
                        onMouseEnter={(e) => {
                          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                          if (e.clientX > rect.right - 30) setHoverEndOfClip(c.id);
                        }}
                        onMouseMove={(e) => {
                          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                          setHoverEndOfClip(e.clientX > rect.right - 30 ? c.id : (hoverEndOfClip === c.id ? null : hoverEndOfClip));
                        }}
                        onMouseLeave={() => { if (hoverEndOfClip === c.id) setHoverEndOfClip(null); }}
                        onContextMenu={(e) => onClipContextMenu(e, c)}
                      >
                        {showWaveform && (
                          <Waveform
                            src={(media!.proxy_url || media!.original_url)!}
                            inMs={c.in} outMs={c.out}
                            width={Math.max(20, width - 16)} height={LANE_HEIGHT}
                            color="rgba(91,199,255,0.5)"
                          />
                        )}
                        <div
                          className="trim-handle"
                          onMouseDown={(e) => onTrimMouseDown(e, c, "in")}
                          style={{ width: 8, cursor: "ew-resize", flexShrink: 0,
                                   background: "linear-gradient(90deg, rgba(91,199,255,0.4), transparent)",
                                   zIndex: 2 }}
                          title="Recortar inicio"
                        />
                        <div
                          onMouseDown={(e) => onClipBodyMouseDown(e, c)}
                          style={{ flex: 1, padding: "0 4px", cursor: "grab",
                                   display: "flex", alignItems: "center", overflow: "hidden",
                                   whiteSpace: "nowrap", textOverflow: "ellipsis", fontSize: 11,
                                   zIndex: 2, position: "relative",
                                   textShadow: showWaveform ? "0 0 4px rgba(0,0,0,0.8)" : undefined }}
                        >
                          {media?.name?.split(/[/\\]/).pop()?.slice(0, 18) || `#${c.clipId}`} · {(len / 1000).toFixed(1)}s
                        </div>
                        <div
                          className="trim-handle"
                          onMouseDown={(e) => onTrimMouseDown(e, c, "out")}
                          style={{ width: 8, cursor: "ew-resize", flexShrink: 0,
                                   background: "linear-gradient(270deg, rgba(91,199,255,0.4), transparent)",
                                   zIndex: 2 }}
                          title="Recortar final"
                        />
                      </div>
                      {/* + button: SIBLING del clip (no atrapado por overflow:hidden), */}
                      {/*  posicionado en el borde derecho del clip dentro del track. */}
                      {showAddButton && (
                        <div
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); setTrPickerForClip(c.id); }}
                          title="Añadir transición"
                          style={{
                            position: "absolute",
                            left: rightEdgeX - 14,
                            top: top + LANE_HEIGHT / 2 - 14,
                            width: 28, height: 28, borderRadius: "50%",
                            background: "#4ade80", color: "#062818",
                            display: "grid", placeItems: "center",
                            fontWeight: 700, fontSize: 20, cursor: "pointer",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.5), 0 0 0 2px var(--bg)",
                            zIndex: 50,
                          }}
                        >+</div>
                      )}
                      {hasTransition && nextClip && (() => {
                        const trDur = c.transitionOut!.duration_ms;
                        const trDef = findTransition(c.transitionOut!.type);
                        const trLabel = trDef?.label || c.transitionOut!.type;
                        const trIcon = trDef?.icon || "▸";
                        const trWidth = Math.max(60, (trDur / 1000) * pxPerSec);
                        const trLeft = rightEdgeX - trWidth / 2;
                        const onResizeStart = (side: "left" | "right") => (e: React.MouseEvent) => {
                          e.stopPropagation();
                          const startX = e.clientX;
                          const startDur = trDur;
                          function onMove(ev: MouseEvent) {
                            const deltaPx = ev.clientX - startX;
                            const deltaMs = (deltaPx / pxPerSec) * 1000;
                            // Ambos lados aumentan o disminuyen la duración total
                            const newDur = side === "left"
                              ? Math.max(100, Math.min(5000, startDur - deltaMs * 2))
                              : Math.max(100, Math.min(5000, startDur + deltaMs * 2));
                            const tl = useEditor.getState().timeline;
                            const tracks = tl.tracks.map((t) => ({
                              ...t,
                              clips: t.clips.map((cc) => cc.id === c.id
                                ? { ...cc, transitionOut: { ...cc.transitionOut!, duration_ms: Math.round(newDur) } }
                                : cc),
                            }));
                            useEditor.getState().setTimeline({ ...tl, tracks });
                          }
                          function onUp() {
                            window.removeEventListener("mousemove", onMove);
                            window.removeEventListener("mouseup", onUp);
                            document.body.style.cursor = "";
                          }
                          document.body.style.cursor = "ew-resize";
                          window.addEventListener("mousemove", onMove);
                          window.addEventListener("mouseup", onUp);
                        };
                        return (
                          <div
                            onClick={(e) => { e.stopPropagation(); selectOne(c.id); }}
                            onDoubleClick={(e) => { e.stopPropagation(); setTrPickerForClip(c.id); }}
                            title={`${trLabel} · ${trDur}ms\nclick: editar en inspector\ndoble click: cambiar transición\narrastra los bordes: ajustar duración`}
                            style={{
                              position: "absolute",
                              left: trLeft,
                              top: top + 2,
                              width: trWidth,
                              height: LANE_HEIGHT - 4,
                              background: "linear-gradient(180deg, #4ade80 0%, #16a34a 100%)",
                              border: "1px solid #14532d",
                              borderRadius: 4,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              gap: 4, padding: "0 4px",
                              fontSize: 10, color: "#062818", fontWeight: 700,
                              cursor: "pointer",
                              zIndex: 45,
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              boxShadow: "0 2px 10px rgba(74,222,128,0.5)",
                            }}
                          >
                            {/* Resize handle izquierdo */}
                            <div
                              onMouseDown={onResizeStart("left")}
                              style={{
                                position: "absolute", left: 0, top: 0, bottom: 0,
                                width: 6, cursor: "ew-resize",
                                background: "rgba(0,0,0,0.15)",
                              }}
                              title="Acortar transición"
                            />
                            <span style={{ fontSize: 12 }}>{trIcon}</span>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1, textAlign: "center" }}>
                              {trLabel} · {trDur}ms
                            </span>
                            {/* Resize handle derecho */}
                            <div
                              onMouseDown={onResizeStart("right")}
                              style={{
                                position: "absolute", right: 0, top: 0, bottom: 0,
                                width: 6, cursor: "ew-resize",
                                background: "rgba(0,0,0,0.15)",
                              }}
                              title="Alargar transición"
                            />
                          </div>
                        );
                      })()}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {ctx && (
        <ContextMenu
          x={ctx.x} y={ctx.y}
          items={ctxItems()}
          onClose={() => setCtx(null)}
        />
      )}

      {trPickerForClip && (
        <TransitionsPanel
          variant="modal"
          onClose={() => setTrPickerForClip(null)}
          onPick={(id) => applyTransitionTo(trPickerForClip, id)}
        />
      )}
    </div>
  );
}
